import { useState } from 'react';
import { gql } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router';

import GET_ROLES_FOR_ACCOUNT_Q from '../gql/getRolesForAccount.gql';
import GET_SUB_ACCOUNTS_FOR_ACCOUNT_Q from '@/features/accounts/gql/getSubAccountsForAccount.gql';

import { TypeOf, z } from 'zod';
import { Controller } from 'react-hook-form';

import { Card } from '@repo/ui/Card';
import { Form } from '@repo/ui/Form';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';
import { TextInputField, PaginatedSelectorInputField } from '@repo/ui/Form';
import { Button } from '@repo/ui/Button';

import { useUser } from '@repo/hooks/useUser';
import { useAccount } from '@/hooks/useAccount';
import type { Mercury } from '@repo/types/Models';

import { passwordRule } from '@repo/util/passwordRule';

const createUserForAccountFormSchema = z
  .object({
    account: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .optional(),
    firstName: z.string().min(2, { message: 'Name must contain at least 2 characters.' }),
    lastName: z.string().min(2, { message: 'Name must contain at least 2 characters.' }),
    email: z.string().email({ message: 'Please submit a valid email address.' }),
    role: z.object({
      label: z.string(),
      value: z.string(),
    }),
    password: passwordRule,
    passwordConfirmation: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Passwords do not match. Please try again.',
    path: ['passwordConfirmation'],
  });

type CreateUserForAccountFormParams = TypeOf<typeof createUserForAccountFormSchema>;

const CreateUser = () => {
  const navigate = useNavigate();
  const currentAccount = useAccount();
  const accountId = currentAccount.id;
  const currentAccountIsSubAccount = currentAccount.parentAccount;

  //GET ROLES FOR SELECT
  const ROLES_PAGE_SIZE = 5;
  const [rolesQueryVars, setRolesQueryVars] = useState<Mercury.GQL.QueryVariables | null>({
    first: ROLES_PAGE_SIZE,
  });
  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery(GET_ROLES_FOR_ACCOUNT_Q, {
    variables: { ...rolesQueryVars, id: accountId },
    notifyOnNetworkStatusChange: true,
  });
  if (rolesError) {
    console.error(rolesError);
  }

  const roles = rolesData?.rolesForAccount.edges.map((role) => {
    return {
      label: role.node.name,
      value: role.node.id,
      description: role.node.notes,
    };
  });

  const pageInfo = rolesData?.rolesForAccount.pageInfo;

  // GET SUB ACCOUNTS FOR SELECT
  const SUB_ACCOUNTS_PAGE_SIZE = 5;
  const [subAccountsQueryVars, setSubAccountsQueryVars] =
    useState<Mercury.GQL.QueryVariables | null>({
      first: SUB_ACCOUNTS_PAGE_SIZE,
    });
  const {
    loading: subAccountsLoading,
    error: subAccountsError,
    data: subAccountsData,
  } = useQuery(GET_SUB_ACCOUNTS_FOR_ACCOUNT_Q, {
    variables: { ...subAccountsQueryVars, id: accountId },
    notifyOnNetworkStatusChange: true,
  });
  if (subAccountsError) {
    console.error(subAccountsError);
  }

  const subAccounts = subAccountsData?.subAccountsForAccount?.edges.map((subAccount) => {
    return {
      label: subAccount.node.name,
      value: subAccount.node.id,
    };
  });

  const user = useUser();
  const allAccounts = Array.isArray(subAccounts)
    ? [...subAccounts, { label: user.account.name, value: user.account.id }]
    : subAccounts;
  const subAccountsPageInfo = subAccountsData?.subAccountsForAccount.pageInfo;

  // Mutation
  const CREATE_USER_WITH_AUTH_PROFILE_FOR_ACCOUNT = gql`
    mutation CreateUserWithAuthProfileForAccount(
      $accountId: ID!
      $roleId: ID
      $input: CreateUserWithAuthProfileForAccountInput!
    ) {
      createUserWithAuthProfileForAccount(roleId: $roleId, accountId: $accountId, input: $input) {
        user {
          id
          account {
            id
          }
        }
      }
    }
  `;

  const client = useApolloClient();
  const onSubmit = async ({ role, account, ...input }: CreateUserForAccountFormParams) => {
    await client.mutate({
      mutation: CREATE_USER_WITH_AUTH_PROFILE_FOR_ACCOUNT,
      variables: {
        input,
        roleId: role.value,
        accountId: currentAccountIsSubAccount || !account ? currentAccount.id : account.value,
      },
    });
    setTimeout(() => {
      navigate(`/a/${accountId}/users`);
    }, 2000);
  };
  const form = useZodForm({
    schema: createUserForAccountFormSchema,
    onSubmit,
  });

  return (
    <>
      <Card>
        <Form {...form}>
          <Form.Success>User successfully created. Redirecting to users page...</Form.Success>
          <Form.Error>
            Unable to create user. Please try again later or contact support if error persists.
          </Form.Error>

          {!currentAccountIsSubAccount && (
            <Controller
              render={({ field: { onChange, value } }) => (
                <PaginatedSelectorInputField
                  loading={subAccountsLoading}
                  id="account"
                  label="Account"
                  onChange={onChange}
                  value={value}
                  error={form?.formState?.errors ? form?.formState?.errors['account']?.value : ''}
                  options={[...new Set(allAccounts)]}
                  pageInfo={subAccountsPageInfo}
                  setQueryVars={setSubAccountsQueryVars}
                  pageSize={SUB_ACCOUNTS_PAGE_SIZE}
                  name="account"
                />
              )}
              control={form.control}
              name="account"
            />
          )}

          <TextInputField label="First name" {...registerInput(form, 'firstName')} />
          <TextInputField label="Last name" {...registerInput(form, 'lastName')} />
          <TextInputField type="email" label="Email" {...registerInput(form, 'email')} />

          <Controller
            render={({ field: { onChange, value } }) => (
              <PaginatedSelectorInputField
                loading={rolesLoading}
                id="role"
                label="Role"
                onChange={onChange}
                value={value}
                error={form?.formState?.errors ? form?.formState?.errors['role']?.value : ''}
                options={roles ?? []}
                pageInfo={pageInfo}
                setQueryVars={setRolesQueryVars}
                pageSize={ROLES_PAGE_SIZE}
                name="role"
              />
            )}
            control={form.control}
            name="role"
          />

          <TextInputField
            type="password"
            label="Password"
            description="Your password must be at least 12 characters long and include a number, a symbol, and a capital letter."
            {...registerInput(form, 'password')}
          />
          <TextInputField
            type="password"
            label="Confirm password"
            {...registerInput(form, 'passwordConfirmation')}
          />

          <div className="w-full">
            <Button type="submit">Create User</Button>
          </div>
        </Form>
      </Card>
    </>
  );
};

export default CreateUser;
